/**
 * **주문의 취소·환불 표시 — 어휘·라벨·조회 조건 한 곳.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 테스트가
 * node 에서 그대로 읽는다).
 *
 * 계약 정본: `ordern-workspace/docs/planning/qr-order-accept-timeout-impl-20260921/wp2-auto-cancel-report.md` §2(어드민 주문 목록
 * `GET /order` 의 행 키·조건). 요약:
 *
 * - 행 키: `cancelDate`(`yyyy-MM-dd HH:mm:ss`, 배포 전 취소는 null) · `cancelActor`(계정 ID, 또는 자동 경로 라벨 — [CANCEL_ACTOR]) ·
 *   `cancelReason`(자동 = `ACCEPT_TIMEOUT`) · `cancelPayFailReason` · `refundStatus`(`NONE|PENDING|DONE`).
 * - 조건 `refundPending=true` — 「환불 실패(수동 처리)만」. 술어의 뜻은 서버가 소유한다(`RefundStatus.PENDING_SINCE_DEPLOY_SQL` —
 *   취소됨·결제됨·환불 안 됨·배포 뒤 취소). 화면은 취소 축(`cancelYn`·`cancelPay`)을 따로 조합하지 않는다.
 * - 정렬 축 `cancelDate`.
 *
 * 서버는 어휘만 내리고 한글 문구는 이 파일이 만든다 — 화면마다 매핑을 적으면 같은 값의 문구가 갈린다.
 */

/**
 * 서버가 싣는 취소 주체 중 **계정이 아닌 것**(자동 경로 라벨) — 서버 `CancelActor` 의 라벨 상수와 같은 문자열이다(`AUTO_CANCEL_LABEL`·
 * `PG_NOTIFICATION_LABEL`·`OTTER_WEBHOOK_LABEL`·`COUPON_DUPLICATE_LABEL`·`BAROGO_CANCEL_LABEL`, 서버 `PlaceHistoryActor.AUTO_LABELS`).
 * 쿠폰 중복 사용·바로고가 끝낸 취소는 R-O(2026-10-01)부터 취소 처리기를 지나며 이 라벨을 남긴다.
 */
export const CANCEL_ACTOR = Object.freeze({
  AUTO: 'order-auto-cancel',
  PG: 'pg-cancel-notification',
  OTTER: 'otter-webhook',
  COUPON_DUPLICATE: 'coupon-duplicate',
  BAROGO: 'barogo-cancel',
})

/** 환불 상태 — 서버 `RefundStatus` 어휘 그대로 */
export const REFUND_STATUS = Object.freeze({
  NONE: 'NONE',
  PENDING: 'PENDING',
  DONE: 'DONE',
})

/**
 * **내부 운영자용 환불 실패 목록** 화면 — 오더엔 내부 계정(admin·mng)이 `refundPending=true` 로 부르면 서버가 매장을 가로지른다
 * (A3 보고서 §6, `OrderListScope.of` — 그 밖의 계정은 자기 매장). 사이드 메뉴 행이 없으므로 라우터 가드는 액션으로 연다.
 */
export const ORDER_REFUND_PENDING_PATH = '/v/business/order/refund-pending'

/**
 * 그 화면이 반드시 부르는 조회(`GET /order`)의 액션 — 라우터 가드의 `meta.requiredAction` 이자 진입 링크를 그릴지의 술어다
 * (「보이는가」와 「열리는가」가 같은 것을 묻는다). admin·mng 에는 서버 SQL 043 이 준다.
 */
export const ORDER_LIST_ACTION = 'OrderForStoreSearch'

/** 내부 목록의 기본 정렬 — 최근에 취소된 것부터(처리할 일의 목록이다) */
export const REFUND_PENDING_DEFAULT_SORT = Object.freeze({ sortProp: 'cancelDate', sortOrder: 'descending' })

/** 화면 문구 — i18n 키(한국어 문장 그대로) */
export const ORDER_REFUND_TEXT = Object.freeze({
  cancelActor: '취소 주체',
  cancelDate: '취소 시각',
  refundStatus: '환불 상태',
  actorAuto: '자동 취소(미수락)',
  actorPg: '결제사 취소',
  actorOtter: '배달앱 취소(오터)',
  actorCouponDuplicate: '자동 취소(쿠폰 중복 사용)',
  actorBarogo: '배달대행 취소(바로고)',
  refundPending: '환불 실패 — 수동 처리 필요',
  refundDone: '환불 완료',
  refundPendingOnly: '환불 실패만',
  pendingListTitle: '환불 실패 주문',
  pendingListIntro: '자동으로 취소됐지만 결제 환불에 실패해 수동 처리가 필요한 주문입니다(전 매장).',
  pendingListPeriod: '주문 기간',
  pendingListEmpty: '환불 실패 주문이 없습니다.',
  statusCodesEmpty: '주문 상태 코드를 받지 못해 조회하지 않았습니다. 검색 버튼으로 다시 시도해 주십시오.',
  storeNm: '매장명',
  storeId: '매장 ID',
})

/** 주체 → 라벨. 계정이 아닌 주체만 이름이 있다 */
const ACTOR_LABELS = Object.freeze({
  [CANCEL_ACTOR.AUTO]: ORDER_REFUND_TEXT.actorAuto,
  [CANCEL_ACTOR.PG]: ORDER_REFUND_TEXT.actorPg,
  [CANCEL_ACTOR.OTTER]: ORDER_REFUND_TEXT.actorOtter,
  [CANCEL_ACTOR.COUPON_DUPLICATE]: ORDER_REFUND_TEXT.actorCouponDuplicate,
  [CANCEL_ACTOR.BAROGO]: ORDER_REFUND_TEXT.actorBarogo,
})

/**
 * 취소 주체의 말 — 자동 경로([CANCEL_ACTOR])는 이름, 그 밖은 계정 ID 그대로, 없으면(배포 전 취소) 빈 문자열. 순수.
 *
 * @param {?string} actor 행의 `cancelActor`
 * @returns {string} i18n 키이거나 계정 ID
 */
export function cancelActorLabel(actor) {
  if (!actor) return ''

  return ACTOR_LABELS[actor] || actor
}

/**
 * 환불 상태의 말 — `PENDING` 은 강조(`pending: true`), `DONE` 은 「환불 완료」, `NONE`·모르는 값은 말하지 않는다(`null`). 순수.
 *
 * @param {?string} status 행의 `refundStatus`
 * @returns {?{label: string, pending: boolean}}
 */
export function refundStatusView(status) {
  if (status === REFUND_STATUS.PENDING) return { label: ORDER_REFUND_TEXT.refundPending, pending: true }
  if (status === REFUND_STATUS.DONE) return { label: ORDER_REFUND_TEXT.refundDone, pending: false }

  return null
}

/** 서버 `DeliveryCallCancelNotice.Result` 어휘 그대로 — 배달대행 업체가 호출 취소를 받아들였나 */
export const DELIVERY_CALL_CANCEL = Object.freeze({
  CANCELED: 'CANCELED',
  FAILED: 'FAILED',
})

/**
 * **주문 취소 200 본문 → 매장에 보일 배달대행 안내** (사용자 결정 2, 2026-10-01). 서버(R-O AP1c)는 주문을 취소하며 진행 중인
 * 배달대행 호출도 함께 취소하고, 업체를 실제로 부른 때만 본문 `{deliveryCallCancel: {result, agencyName, message}}` 를 싣는다.
 * 문장(`message`)은 서버가 소유한다 — 화면은 만들지 않고 그대로 보인다.
 *
 * - `failed: true` — 매장이 배달대행에 전화하거나 대행 앱에서 직접 취소해야 한다(화면은 닫아야 사라지는 확인창). 지나가는 안내로
 *   보일 것은 업체가 받아들인 `CANCELED` 하나뿐이라 모르는 결과도 이쪽이다.
 * - `failed: false` — 함께 취소했다(짧은 안내).
 * - `null` — 업체를 부르지 않은 취소(본문 없는 200 — axios 는 빈 문자열)·칸 없음·문장 없음. 화면은 종전 「주문 취소 성공」.
 *
 * 순수 — 인자를 바꾸지 않는다.
 *
 * @param {*} body 주문 취소 응답 본문(legacy 헬퍼가 성공 콜백에 넘기는 값)
 * @returns {?{failed: boolean, message: string}}
 */
export function deliveryCallCancelNotice(body) {
  const notice = body && typeof body === 'object' ? body.deliveryCallCancel : null
  const message = notice && typeof notice.message === 'string' ? notice.message : ''
  if (!message.trim()) return null

  return Object.freeze({ failed: notice.result !== DELIVERY_CALL_CANCEL.CANCELED, message })
}

/**
 * 취소·환불 칸을 그릴 것인가 — 받은 행 가운데 취소된 것이 있을 때만. 기본 조건(「주문 취소 여부: N」)의 목록에는 빈 칸만 늘기
 * 때문이다. 순수.
 *
 * @param {?Array<object>} rows
 */
export function hasCancelledRow(rows) {
  return Array.isArray(rows) && rows.some((row) => !!row && row.cancelYn === 'Y')
}

/**
 * **매장 주문 목록의 조회 조건** — 「환불 실패만」(`refundPending`)이 켜져 있으면 `refundPending=true` 를 싣고 취소 축은 비운다
 * (뜻은 서버 술어가 소유한다 — 화면의 「주문 취소 여부: N」 이 함께 가면 빈 목록이 된다). 꺼져 있으면 그 키를 싣지 않는다(종전
 * 조회 그대로). 순수 — 인자를 바꾸지 않는다.
 *
 * @param {object} query 화면의 조회 조건(페이징 축 포함)
 * @returns {object}
 */
export function orderListQuery(query) {
  const { refundPending, ...rest } = query || {}

  return refundPending ? Object.assign({}, rest, { cancelYn: '', cancelPay: '', refundPending: true }) : rest
}

/**
 * **내부 운영자용 환불 실패 목록의 조회 조건** — `refundPending=true` 와 주문 상태 코드 전부(서버 필수 — 없으면 조회를 낼 수
 * 없다), 기간(주문 시각, 그날 처음부터 끝까지 — 없으면 기간 키 없음), 페이징 축. 상태 코드를 모르면 `null`(부르지 않는다). 순수.
 *
 * @param {{period: ?Array<string>, orderStatusCds: Array<number>, paging: object}} input
 *   `period` — `['YYYY-MM-DD', 'YYYY-MM-DD']` 또는 없음, `paging` — `utils/pagedList.pagingQuery` 의 결과
 * @returns {?object}
 */
export function refundPendingListQuery(input) {
  const { period, orderStatusCds, paging } = input || {}
  if (!Array.isArray(orderStatusCds) || orderStatusCds.length === 0) return null

  const range =
    Array.isArray(period) && period[0] && period[1]
      ? { startDate: `${period[0]} 00:00:00`, endDate: `${period[1]} 23:59:59` }
      : {}

  return Object.assign({ refundPending: true, orderStatusCds: orderStatusCds.join(',') }, range, paging || {})
}
